<template>
  <div>
    <div
      class="flex items-center justify-between mb-6 sm:px-6 sm:mb-2 sm:mt-4"
      v-if="from"
    >
      <a
        :href="prevMonthLink()"
        class="month-navigation"
        :class="{ 'month-navigation--disabled': !showPreviousMonth }"
      >
        <button class="btn btn-outlined btn-default flex items-center text-sm">
          <svg width="24" height="24">
            <use xlink:href="/img/sprite.svg#ic_chevron-left"></use>
          </svg>
          <span class="pr-3">{{ previousMonthText }}</span>
        </button>
      </a>

      <a :href="nextMonthLink()" class="month-navigation">
        <button class="btn btn-outlined btn-default flex items-center text-sm">
          <span class="pl-3">{{ nextMonthText }}</span>
          <svg width="24" height="24">
            <use xlink:href="/img/sprite.svg#ic_chevron-right"></use>
          </svg>
        </button>
      </a>
    </div>

    <div>
      <template v-if="hasResult">
        <div v-if="view == 'boats'">
          <ul class="list-reset result-list">
            <li
              v-for="(resort, index) in resortChunks[page - 1]"
              :key="resort.objectID"
            >
              <responsive>
                <template #lgmd>
                  <loading-item v-if="isLoading" />
                  <template v-else>
                    <liveaboard-item-large :resort="resort" />
                    <contact-block
                      v-if="sortedResults.length > 3 && index === 2"
                      product="liveaboard"
                    />
                  </template>
                </template>
                <template #sm>
                  <loading-item-mobile v-if="isLoading" />
                  <liveaboard-item-small v-else :resort="resort" />
                  <contact-block
                    v-if="sortedResults.length > 3 && index === 2"
                    product="liveaboard"
                  />
                </template>
              </responsive>

              <hr
                class="separator-slim my-10"
                v-if="index !== resortChunks[page - 1].length - 1"
              />
            </li>
          </ul>
        </div>

        <div v-else>
          <ul class="list-reset result-list">
            <li v-for="(data, index) in tripChunks[page - 1]" :key="data.dates">
              <responsive>
                <template #lgmd>
                  <loading-item v-if="isLoading" />
                  <template v-else>
                    <liveaboard-trip-by-date :data="data" />
                    <contact-block
                      v-if="sortedResults.length > 3 && index === 2"
                      product="liveaboard"
                    />
                    <hr
                      v-if="sortedResults.length > 3 && index === 2"
                      class="separator-slim my-10"
                    />
                  </template>
                </template>
                <template #sm>
                  <loading-item-mobile v-if="isLoading" />
                  <template v-else>
                    <liveaboard-trip-by-date-small :data="data" />
                    <contact-block
                      v-if="sortedResults.length > 3 && index === 2"
                      product="resort"
                    />
                  </template>
                </template>
              </responsive>

              <hr
                class="separator-slim my-10"
                v-if="index !== tripChunks[page - 1].length - 1 && isLoading"
              />
            </li>
          </ul>
        </div>

        <responsive>
          <template #lg>
            <div class="flex items-center my-10">
              <div class="flex-1">
                <pagination
                  @click="onSetPage"
                  :pagination-info="paginationInfo"
                />
              </div>

              <div class="flex-none">
                {{ paginationStatus }}
              </div>
            </div>
          </template>
          <template #mdsm>
            <mobile-pagination
              class="sm:px-6"
              :pagination-info="paginationInfo"
            />
          </template>
        </responsive>
      </template>

      <template v-else-if="isLoading === true">
        <ul class="list-reset lg:mt-24">
          <li v-for="index in 10" :key="index">
            <responsive>
              <template #lgmd>
                <loading-item v-if="isLoading" />
              </template>
              <template #sm>
                <loading-item-mobile v-if="isLoading" />
              </template>
            </responsive>

            <hr class="separator-slim my-10" v-if="index !== 10" />
          </li>
        </ul>
      </template>

      <template v-else-if="hasNoResult">
        <div class="container-sm text-center">
          <h3 class="fs-24 font-medium mb-6">
            We haven't found any liveaboards matching your criteria.
          </h3>
          <div class="inline-flex gap-4 sm:w-full sm:flex-wrap">
            <button
              type="button"
              class="btn btn-secondary sm:w-full"
              @click="handleViewUpcomingTrips"
            >
              View upcoming trips
            </button>

            <a
              v-if="productAvailability.hasResorts"
              :href="resortURL"
              class="sm:w-full"
            >
              <button type="button" class="btn btn-secondary w-full">
                View resorts
              </button>
            </a>
            <a
              v-if="productAvailability.hasEcoventures"
              :href="ecoventureURL"
              class="sm:w-full"
            >
              <button type="button" class="btn btn-secondary w-full">
                View ecoventures
              </button>
            </a>
            <a
              v-if="productAvailability.hasYachts"
              :href="yachtURL"
              class="sm:w-full"
            >
              <button type="button" class="btn btn-secondary w-full">
                View yachts
              </button>
            </a>
          </div>
        </div>

        <speak-with-us class="mt-10" />
      </template>
    </div>
  </div>
</template>

<script>
import chunk from "lodash/chunk";
import Pagination from "../../components/core/Pagination";
import Responsive from "../../components/core/Responsive";
import CustomSelect from "../../components/core/CustomSelect";
import LiveaboardItemLarge from "./LiveaboardItemLarge";
import LoadingItem from "./LoadingItem";
import LoadingItemMobile from "./LoadingItemMobile";
import LiveaboardItemSmall from "./LiveaboardItemSmall";
import LiveaboardTripByDate from "./LiveaboardTripByDate";
import LiveaboardTripByDateSmall from "./LiveaboardTripByDateSmall";
import MobilePagination from "./MobilePagination";
import SpeakWithUs from "../../components/Search/SpeakWithUs";
import ContactBlock from "./ContactBlock";
import { mapState, mapMutations, mapGetters } from "vuex";
import { syncURLParams } from "../../helpers/searchHelpers";
import { convertToOtherSearchProduct } from "../../helpers/searchHelpers";

const PER_PAGE = 12;

export default {
  components: {
    Pagination,
    Responsive,
    LoadingItem,
    LoadingItemMobile,
    LiveaboardItemSmall,
    LiveaboardItemLarge,
    MobilePagination,
    CustomSelect,
    SpeakWithUs,
    LiveaboardTripByDate,
    LiveaboardTripByDateSmall,
    ContactBlock,
  },
  data() {
    return {
      sortOptions: [
        {
          label: "Special offers",
          value: "special-offers",
          class: "text-purple",
        },
        { label: "Price (lowest first)", value: "price" },
        { label: "Top Rated", value: "top-rated" },
      ],
      productAvailability: {},
    };
  },
  computed: {
    ...mapState([
      "destination",
      "ranking",
      "page",
      "statistics",
      "isLoading",
      "from",
      "view",
    ]),
    ...mapGetters(["sortedResults", "selectedMonth", "tripsBydates"]),
    tripChunks() {
      return chunk(this.tripsBydates, PER_PAGE);
    },
    sortBy: {
      get() {
        return this.ranking;
      },
      set(val) {
        this.setRanking(val);
        this.setPage(1);
      },
    },
    searchSummary() {
      return `<h1 class="inline-block">${this.destination.name}</h1>: ${this.sortedResults.length} liveaboards`;
    },
    resortChunks() {
      return chunk(this.sortedResults, PER_PAGE);
    },
    paginationInfo() {
      const total =
        this.view === "boats"
          ? this.sortedResults.length
          : this.tripsBydates.length;
      return {
        page: this.page,
        per_page: PER_PAGE,
        total,
      };
    },
    paginationStatus() {
      const { page, per_page, total } = this.paginationInfo;
      const from = (page - 1) * per_page + 1;
      const to = Math.min(page * per_page, total);

      return `Showing ${from} to ${to} of ${total}`;
    },
    hasNoResult() {
      return (
        Boolean(this.statistics) &&
        ((this.view === "boats" && this.sortedResults.length === 0) ||
          (this.view === "dates" && this.tripsBydates.length === 0)) &&
        this.isLoading === false
      );
    },
    hasResult() {
      if (this.view === "boats") {
        return this.sortedResults.length > 0;
      } else {
        return this.tripsBydates.length > 0;
      }
    },
    showPreviousMonth() {
      const today = moment();

      return today.isBefore(this.from, "month");
    },
    previousMonthText() {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has("from")) {
        const from = searchParams.get("from");
        return moment(from).subtract(1, "months").format("MMM YYYY");
      }

      return "Previous month";
    },
    nextMonthText() {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has("from")) {
        const from = searchParams.get("from");
        return moment(from).add(1, "months").format("MMM YYYY");
      }

      return "Next month";
    },
    resortURL() {
      return convertToOtherSearchProduct("liveaboard", "resort");
    },
    ecoventureURL() {
      return convertToOtherSearchProduct("liveaboard", "ecoventure");
    },
    yachtURL() {
      return convertToOtherSearchProduct("liveaboard", "yacht");
    },
  },
  mounted() {
    this.$store.dispatch("loadSuppliers");
    this.$store.dispatch("loadStatistics");

    if (window.zubluDestination) {
      const url = `${window.ZUBLU_VARIABLES.ODYSSEY_API}/api/v2/products_availability_query`;
      const payload = {
        destination_structure: window.zubluDestination,
      };

      $.post(url, payload).then((response) => {
        this.productAvailability = response;
      });
    }
  },
  methods: {
    ...mapMutations(["setRanking", "setPage"]),
    onSetPage(p) {
      this.$store.commit("setPage", p);
      document.querySelector(".search-result").scrollIntoView({
        behavior: "smooth",
      });
    },
    handleViewUpcomingTrips() {
      window.topNavStore.commit("setFormData", {
        key: "dates",
        value: [null, null],
      });

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("from");
      searchParams.delete("budgets");
      searchParams.delete("destinations");
      searchParams.delete("rating");
      searchParams.delete("facilities");
      searchParams.delete("species");
      searchParams.delete("tripLengths");
      searchParams.delete("page");

      syncURLParams(searchParams);
      window.location.reload();
    },
    nextMonthLink() {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has("from")) {
        const from = searchParams.get("from");
        const mmFrom = moment(from, "YYYY-MM-DD");
        const nextMonth = mmFrom.add(1, "months");

        searchParams.set("from", nextMonth.format("YYYY-MM-DD"));
      }

      if (searchParams.get("view") === "dates") {
        searchParams.set("page", 1);
      }

      return (
        window.location.pathname +
        (searchParams.toString() ? `?${searchParams.toString()}` : "")
      );
    },
    prevMonthLink() {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has("from")) {
        const from = searchParams.get("from");
        const mmFrom = moment(from, "YYYY-MM-DD");
        const nextMonth = mmFrom.subtract(1, "months");

        searchParams.set("from", nextMonth.format("YYYY-MM-DD"));
      }

      if (searchParams.get("view") === "dates") {
        searchParams.set("page", 1);
      }

      return (
        window.location.pathname +
        (searchParams.toString() ? `?${searchParams.toString()}` : "")
      );
    },
  },
};
</script>
